import { applyAction, actionForKey, blockAt } from './toolbar'
import { Icon } from '../Icon'

/**
 * Панель форматирования над Markdown-исходником — в манере Confluence:
 * слева выпадающий стиль абзаца, дальше группы кнопок с разделителями.
 *
 * Панель не знает про состояние редактора — она получает ref
 * на textarea и колбэк onChange. Так один компонент обслуживает
 * и статью базы знаний, и правовой документ в настройках, у которых
 * состояние устроено по-разному.
 *
 * Кнопки не отбирают фокус у textarea (preventDefault на mousedown):
 * иначе выделение схлопнулось бы раньше, чем действие его прочитает.
 *
 * Кнопкам соответствуют хоткеи Ctrl/Cmd+B, +I, +K — их вешает
 * редактор через handleToolbarKeydown на саму textarea.
 */

const GROUPS = [
  [
    { action: 'bold', icon: 'bold', label: 'Жирный (Ctrl+B)' },
    { action: 'italic', icon: 'italic', label: 'Курсив (Ctrl+I)' },
    { action: 'strike', icon: 'strike', label: 'Зачёркнутый' },
    { action: 'code', icon: 'codeInline', label: 'Код' },
  ],
  [
    { action: 'ul', icon: 'listUl', label: 'Маркированный список' },
    { action: 'ol', icon: 'listOl', label: 'Нумерованный список' },
    { action: 'tasks', icon: 'tasks', label: 'Чек-лист' },
    { action: 'quote', icon: 'quote', label: 'Цитата' },
  ],
  [
    { action: 'outdent', icon: 'outdent', label: 'Убрать отступ' },
    { action: 'indent', icon: 'indent', label: 'Сделать отступ' },
  ],
  [
    { action: 'align:left', icon: 'alignLeft', label: 'По левому краю' },
    { action: 'align:center', icon: 'alignCenter', label: 'По центру' },
    { action: 'align:right', icon: 'alignRight', label: 'По правому краю' },
  ],
  [
    { action: 'link', icon: 'link', label: 'Ссылка (Ctrl+K)' },
    { action: 'table', icon: 'table', label: 'Таблица' },
    { action: 'hr', icon: 'minusLine', label: 'Разделительная линия' },
  ],
]

/** Меню вставки «+» — как в Confluence. */
const INSERTS = [
  ['panel:info', 'Панель — информация'],
  ['panel:note', 'Панель — заметка'],
  ['panel:warning', 'Панель — предупреждение'],
  ['panel:success', 'Панель — успех'],
  ['panel:error', 'Панель — ошибка'],
  ['expand', 'Разворачиваемый блок'],
  ['toc', 'Оглавление'],
]

const BLOCKS = [
  ['p', 'Абзац'],
  ['h1', 'Заголовок 1'],
  ['h2', 'Заголовок 2'],
  ['h3', 'Заголовок 3'],
  ['h4', 'Заголовок 4'],
]

/** Применить действие к textarea и вернуть фокус с выделением. */
function run(textareaRef, onChange, action) {
  const el = textareaRef.current
  if (!el) return
  const next = applyAction(action, el.value, el.selectionStart, el.selectionEnd)
  onChange(next.text)
  // Выделение выставляется после того, как React дорисует value.
  requestAnimationFrame(() => {
    el.focus()
    el.setSelectionRange(next.start, next.end)
  })
}

/** Обработчик хоткеев — редактор вешает его на textarea. */
export function handleToolbarKeydown(e, textareaRef, onChange) {
  const action = actionForKey(e)
  if (!action) return false
  e.preventDefault()
  run(textareaRef, onChange, action)
  return true
}

export function MarkdownToolbar({ textareaRef, onChange, extra = null }) {
  const currentBlock = () => {
    const el = textareaRef.current
    return el ? blockAt(el.value, el.selectionStart) : 'p'
  }

  return (
    <div className="md-toolbar" role="toolbar" aria-label="Форматирование">
      <select
        className="md-toolbar__block"
        aria-label="Стиль абзаца"
        // value не контролируется: панель без состояния, а стиль
        // в точке курсора она узнаёт в момент выбора.
        defaultValue="p"
        onMouseDown={(e) => {
          // Синхронизировать подсветку со строкой курсора при открытии.
          e.currentTarget.value = currentBlock()
        }}
        onChange={(e) => {
          run(textareaRef, onChange, e.target.value)
          e.target.value = 'p'
        }}
      >
        {BLOCKS.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>

      <select
        className="md-toolbar__block md-toolbar__insert"
        aria-label="Вставить элемент"
        value=""
        onChange={(e) => {
          if (e.target.value) run(textareaRef, onChange, e.target.value)
        }}
      >
        <option value="" disabled>+ Вставить</option>
        {INSERTS.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>

      {GROUPS.map((group, gi) => (
        <div className="md-toolbar__group" key={gi}>
          {group.map((b) => (
            <button
              key={b.action}
              type="button"
              className="md-toolbar__btn"
              title={b.label}
              aria-label={b.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => run(textareaRef, onChange, b.action)}
            >
              <Icon name={b.icon} size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
      ))}

      {extra && <div className="md-toolbar__extra">{extra}</div>}
    </div>
  )
}
